import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Sparkles, Lock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/reset-password")({
  head: () => ({
    meta: [
      { title: "Sätt nytt lösenord — Inkubera" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: ResetPasswordPage,
});

function ResetPasswordPage() {
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);
  const [hasSession, setHasSession] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    // Supabase sätter en recovery-session via URL-fragment.
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "PASSWORD_RECOVERY" || session) {
        setHasSession(true);
      }
      setReady(true);
    });
    supabase.auth.getSession().then(({ data }) => {
      setHasSession(!!data.session);
      setReady(true);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError("Lösenordet måste vara minst 8 tecken.");
      return;
    }
    if (password !== confirm) {
      setError("Lösenorden matchar inte.");
      return;
    }
    setSubmitting(true);
    const { error } = await supabase.auth.updateUser({ password });
    setSubmitting(false);
    if (error) {
      setError(error.message);
      return;
    }
    setDone(true);
    // Skicka användaren till rätt portal baserat på roll
    const { data: userData } = await supabase.auth.getUser();
    let dest: "/oversikt" | "/coach-portal" | "/bolag-portal" = "/oversikt";
    if (userData.user) {
      const { data: roles } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", userData.user.id);
      const roleSet = new Set((roles ?? []).map((r) => r.role));
      if (roleSet.has("admin")) dest = "/oversikt";
      else if (roleSet.has("coach")) dest = "/coach-portal";
      else if (roleSet.has("company")) dest = "/bolag-portal";
    }
    setTimeout(() => navigate({ to: dest }), 1200);
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <header className="px-8 py-6">
        <Link to="/" className="inline-flex items-center gap-2.5">
          <div className="size-9 rounded-xl bg-foreground flex items-center justify-center text-background">
            <Sparkles className="size-4" strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-display text-lg leading-none">Inkubera</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mt-0.5">
              Portfolio Intelligence
            </div>
          </div>
        </Link>
      </header>

      <main className="flex-1 flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          <div className="rounded-2xl border border-border bg-card p-8">
            <div className="size-10 rounded-xl bg-secondary flex items-center justify-center mb-5">
              <Lock className="size-4" />
            </div>
            <h1 className="font-display text-2xl">Sätt nytt lösenord</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Välj ett nytt lösenord för ditt konto.
            </p>

            {!ready ? (
              <div className="mt-6 text-sm text-muted-foreground">Laddar…</div>
            ) : !hasSession ? (
              <div className="mt-6 rounded-lg bg-destructive/10 border border-destructive/30 text-destructive p-4 text-sm">
                Återställningslänken är ogiltig eller har gått ut. Begär en ny från{" "}
                <Link to="/forgot-password" className="underline">
                  Glömt lösenord
                </Link>.
              </div>
            ) : done ? (
              <div className="mt-6 rounded-lg bg-secondary p-4 text-sm">
                Lösenordet har uppdaterats. Skickar dig vidare…
              </div>
            ) : (
              <form onSubmit={onSubmit} className="mt-6 space-y-4">
                <div>
                  <label className="text-xs uppercase tracking-wider text-muted-foreground">
                    Nytt lösenord
                  </label>
                  <input
                    type="password"
                    autoComplete="new-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="mt-1 w-full h-10 px-3 rounded-lg bg-secondary border border-transparent focus:border-ring focus:outline-none text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-muted-foreground">
                    Bekräfta lösenord
                  </label>
                  <input
                    type="password"
                    autoComplete="new-password"
                    required
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    className="mt-1 w-full h-10 px-3 rounded-lg bg-secondary border border-transparent focus:border-ring focus:outline-none text-sm"
                  />
                </div>
                {error && (
                  <div className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-lg px-3 py-2">
                    {error}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full h-10 rounded-lg bg-foreground text-background text-sm font-medium hover:opacity-90 disabled:opacity-50"
                >
                  {submitting ? "Sparar…" : "Spara lösenord"}
                </button>
              </form>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
